import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  Check,
  Loader2,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

// ===========================================================================
// En qué anda la lectura, en una línea.
//
// Sustituye al tablero de cinco columnas por etapas. Tres de esas columnas
// estaban casi siempre vacías, dos eran conceptos de vouchers que en la mitad
// de extractos no significaban nada, y ninguna contestaba la pregunta del mes.
//
// Lo que sí hacía falta conservar son sus DOS ACCIONES: volver a leer lo que
// falló, y pagar al modelo por las imágenes que el lector óptico no resolvió.
// Sin ellas, un archivo que falla al subir se queda muerto y el cruce devuelve
// cero sin que nada diga que falta un paso.
// ===========================================================================

/** Una columna del pipeline, de las que devuelve `/documentos`. */
type Columna = {
  clave: string;
  cuantos: number;
  tarjetas: Array<{ id: string; titulo: string; detalle: string | null }>;
};

export function BandaDePipeline({
  clase,
}: {
  clase: "estado_cuenta" | "comprobante";
}) {
  const { data } = api.documentos.useQuery(["pipeline", clase], {
    query: { clase },
  });
  const d = data?.status === 200 ? data.body : null;
  const qc = useQueryClient();
  const reintentar = api.reintentarFallados.useMutation();
  const conModelo = api.leerConModelo.useMutation();
  const [verFallados, setVerFallados] = useState(false);

  if (!d) return null;

  const columnas = d.columnas as Columna[];
  const fallados = columnas.find((c) => c.clave === "problema");
  const enCurso = columnas.find((c) => c.clave === "leyendo")?.cuantos ?? 0;
  const leidos = columnas.find((c) => c.clave === "leidos")?.cuantos ?? 0;
  const n = (v: number) => v.toLocaleString("es-PE");

  const nada =
    (fallados?.cuantos ?? 0) === 0 &&
    enCurso === 0 &&
    (d.lectura?.paraElModelo ?? 0) === 0 &&
    (d.lectura?.sinLeer ?? 0) === 0;

  // Todo leído y nada que pagar: una línea verde y se acabó. La banda existe
  // para avisar, no para ocupar sitio cuando no hay nada que decir.
  if (nada && leidos > 0) {
    return (
      <p className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
        <Check className="size-3.5 shrink-0 text-emerald-600" />
        {n(leidos)} archivo{leidos === 1 ? "" : "s"} leído
        {leidos === 1 ? "" : "s"}, nada pendiente de procesar.
      </p>
    );
  }
  if (nada) return null;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border bg-muted/30 px-3.5 py-2.5 text-[12px]">
        <span className="font-medium text-muted-foreground">La lectura</span>
        <span className="tabular-nums">
          <b>{n(leidos)}</b> leídos
        </span>

        {enCurso > 0 ? (
          <span className="flex items-center gap-1.5 text-cyan-700 tabular-nums dark:text-cyan-400">
            <Loader2 className="size-3.5 shrink-0 animate-spin" />
            <b>{n(enCurso)}</b> en curso
          </span>
        ) : null}

        {/* Lo que ni se miró: pasa cuando el lector se cae a mitad de una
            carga. El aviso lleva su arreglo al lado, que es reintentar. */}
        {(d.lectura?.sinLeer ?? 0) > 0 ? (
          <span className="flex items-center gap-2 rounded border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-red-700 dark:text-red-400">
            {n(d.lectura?.sinLeer ?? 0)} imágenes sin leer siquiera
          </span>
        ) : null}

        {/* El daño silencioso: la lectura no las llamó voucher y traen
            importe, banco o nº de operación. El cruce exige `clase = voucher`,
            así que ese pago no se concilia aunque el importe esté bien leído.
            Medido antes de arreglarlo: 41 imágenes en 42 expedientes, 29 con
            importe y NINGUNA cruzada. Las arregla el mismo botón del modelo. */}
        {(d.lectura?.malClasificadas ?? 0) > 0 ? (
          <span
            className="flex items-center gap-1.5 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-amber-700 dark:text-amber-400"
            title="Traen señas de pago pero la lectura no las clasificó como voucher, así que el cruce no las mira. Releerlas con el modelo suele corregirlo."
          >
            <AlertTriangle className="size-3.5 shrink-0" />
            {n(d.lectura?.malClasificadas ?? 0)} con señas de pago mal
            clasificadas
          </span>
        ) : null}

        {(fallados?.cuantos ?? 0) > 0 ? (
          <button
            type="button"
            onClick={() => setVerFallados((x) => !x)}
            className="flex items-center gap-1.5 rounded border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-red-700 hover:bg-red-500/20 dark:text-red-400"
          >
            <AlertTriangle className="size-3.5 shrink-0" />
            {n(fallados?.cuantos ?? 0)} no se pudieron leer
          </button>
        ) : null}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* Pagar al modelo. El precio va DELANTE del botón: se cobra por
              llamada, y un botón que gasta dinero sin decir cuánto no se
              pulsa dos veces. */}
          {(d.lectura?.paraElModelo ?? 0) > 0 ? (
            <Button
              size="sm"
              variant="outline"
              disabled={conModelo.isPending}
              onClick={() =>
                conModelo.mutate(
                  { body: { confirmo: true } },
                  {
                    onSuccess: (r: { body: { dice?: string } }) => {
                      toast.success(r.body.dice ?? "Leído");
                      qc.invalidateQueries();
                    },
                    onError: () => toast.error("No se pudo leer con el modelo"),
                  },
                )
              }
            >
              <Sparkles className="mr-1.5 size-3.5" />
              {conModelo.isPending
                ? "Leyendo…"
                : `Leer ${n(d.lectura?.paraElModelo ?? 0)} con el modelo (≈ $${(d.lectura?.costoEstimado ?? 0).toFixed(2)})`}
            </Button>
          ) : null}

          {(fallados?.cuantos ?? 0) > 0 || (d.lectura?.sinLeer ?? 0) > 0 ? (
            <Button
              size="sm"
              variant="outline"
              disabled={reintentar.isPending}
              onClick={() =>
                reintentar.mutate(
                  { body: { confirmo: true } },
                  {
                    onSuccess: (r: { body: { dice?: string } }) => {
                      toast.success(r.body.dice ?? "Encolado");
                      qc.invalidateQueries();
                    },
                    onError: () => toast.error("No se pudo reencolar"),
                  },
                )
              }
            >
              <RefreshCw className="mr-1.5 size-3.5" />
              Volver a leer lo que falló
            </Button>
          ) : null}
        </div>
      </div>

      {/* Cuáles fallaron, con su error. Ninguno se descarta solo: un archivo
          ilegible no es un archivo que no existe, y hay que poder pedir el
          original sabiendo cuál es. */}
      {verFallados && fallados ? (
        <ul className="space-y-1 rounded-lg border bg-card p-3 text-[12px]">
          {fallados.tarjetas.map((t) => (
            <li key={t.id}>
              <Link
                to="/archivo/$id"
                params={{ id: t.id }}
                className="font-medium hover:underline"
              >
                {t.titulo}
              </Link>
              {t.detalle ? (
                <span className="ml-2 text-muted-foreground">{t.detalle}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
